import {
  useCallback,
  useLayoutEffect,
  useRef,
  type ChangeEvent,
  type KeyboardEvent,
  type Ref,
  type TextareaHTMLAttributes,
} from "react";

import "./composer.css";
import "./focus.css";
import { applyHomeEndKey } from "../../lib/home-end-caret";
import { isCoarsePointer, shouldSendOnEnter } from "../../lib/send-on-enter";
import { cn } from "./cn";

export type ComposerSize = "sm" | "md" | "lg";

export interface ComposerProps extends Omit<
  TextareaHTMLAttributes<HTMLTextAreaElement>,
  "rows" | "size" | "onSubmit"
> {
  size?: ComposerSize;
  /** Initial visible rows; the field auto-grows with content from here. */
  minRows?: number;
  /** Fires on Enter (without Shift, desktop only) when `submitOnEnter` is set. */
  onSubmit?: () => void;
  submitOnEnter?: boolean;
  ref?: Ref<HTMLTextAreaElement>;
}

/**
 * Auto-growing multi-line composer (the chat/agent message input). Token-driven; one
 * focus-visible ring, reduced-motion aware. Height tracks content via JS (works in every
 * browser and controlled/uncontrolled); with `submitOnEnter`, Enter submits and Shift+Enter
 * inserts a newline — except on coarse-pointer (touch) devices, where the on-screen keyboard
 * has no Shift, so Enter always inserts a newline and only the send button submits.
 */
export function Composer({
  size = "md",
  minRows = 1,
  value,
  defaultValue,
  onChange,
  onKeyDown,
  onSubmit,
  submitOnEnter = false,
  className,
  ref,
  ...rest
}: ComposerProps) {
  const innerRef = useRef<HTMLTextAreaElement | null>(null);

  const setRef = useCallback(
    (node: HTMLTextAreaElement | null) => {
      innerRef.current = node;
      if (typeof ref === "function") ref(node);
      else if (ref) ref.current = node;
    },
    [ref],
  );

  const autoGrow = useCallback(() => {
    const ta = innerRef.current;
    if (!ta) return;
    ta.style.height = "auto";
    ta.style.height = `${ta.scrollHeight}px`;
  }, []);

  // Resize on mount and whenever the (controlled) value changes.
  useLayoutEffect(() => {
    autoGrow();
  }, [autoGrow, value, defaultValue]);

  const handleChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
    onChange?.(event);
    autoGrow();
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    onKeyDown?.(event);
    // A consumer's onKeyDown that already consumed this Enter (preventDefault)
    // owns the keystroke — submitting here too would double-send the message.
    if (submitOnEnter && !event.defaultPrevented && shouldSendOnEnter(event, isCoarsePointer())) {
      event.preventDefault();
      onSubmit?.();
      return;
    }
    // Home/End move the caret within the line on every platform (macOS gives the
    // field nothing of its own). No-ops if the consumer already consumed the key.
    applyHomeEndKey(event);
  };

  return (
    <textarea
      ref={setRef}
      rows={minRows}
      value={value}
      defaultValue={defaultValue}
      onChange={handleChange}
      onKeyDown={handleKeyDown}
      className={cn("rc-composer", `rc-composer--${size}`, className)}
      {...rest}
    />
  );
}
